import { Injectable } from '@angular/core';
import { AssignmentSectionQuestionQuery, AssignmentSectionQuestionService } from '@examdojo/assignment';
import { ChallengeActivityQuestionAttemptQuery, ChallengeActivityQuestionAttemptService } from '@examdojo/challenge';
import { shareOneReplay } from '@examdojo/core/rxjs';
import {
  calculateQuestionTotalMarks,
  getGradingResultAwardedMarksFromQuestionItems,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptEntityUIModel,
} from '@examdojo/question';
import { map, Observable } from 'rxjs';
import {
  QuestionGradingResultData,
  QuestionsWithAttemptStatuses,
} from '../question-selector/challenge-activity-question-selector.service';

export interface ReviewQuestionsWithAttemptStatuses extends QuestionsWithAttemptStatuses {
  questionAttemptId: string | null;
}

@Injectable()
export class ChallengeReviewQuestionSelectorService {
  constructor(
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly assignmentSectionQuestionService: AssignmentSectionQuestionService,
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
  ) {}

  readonly questionsWithAttemptStatuses$: Observable<ReviewQuestionsWithAttemptStatuses[]> =
    this.questionAttemptQuery.entities$.pipe(
      map((questionAttemptEntities) => this.buildQuestionsWithAttemptStatuses(questionAttemptEntities)),
      shareOneReplay(),
    );

  selectQuestion(question: ReviewQuestionsWithAttemptStatuses, challengeActivityId: string): void {
    if (!question.questionAttemptId) {
      return;
    }

    this.assignmentSectionQuestionService.setActiveQuestion(
      question.assignmentSectionQuestion.assignment_section_id,
      question.assignmentSectionQuestion.question_id,
    );

    this.challengeActivityQuestionAttemptService.setActive(
      challengeActivityId,
      question.assignmentSectionQuestion.question_id,
    );

    this.questionAttemptEntityService.setActive(question.questionAttemptId);
  }

  private buildQuestionsWithAttemptStatuses(
    questionAttemptEntities: QuestionAttemptEntityUIModel[],
  ): ReviewQuestionsWithAttemptStatuses[] {
    const questions = this.assignmentSectionQuestionQuery.getAllEntities();
    const attempts = this.challengeActivityQuestionAttemptQuery.getAllEntities();

    const relevantAttemptEntities = questionAttemptEntities.filter((entity) =>
      attempts.some((attempt) => attempt.question_id === entity.attempt.question_id),
    );

    return questions.map((question) => {
      const attempt = attempts.find((a) => a.question_id === question.question_id);
      const questionAttemptEntity = relevantAttemptEntities.find(
        (entity) => entity.attempt.question_id === question.question_id,
      );

      return {
        assignmentSectionQuestion: question,
        attemptStatus: attempt?.status ?? 'DONE',
        questionAttemptStatus: questionAttemptEntity?.attempt.status ?? null,
        questionGradingResult: this.getGradingResult(questionAttemptEntity),
        questionAttemptId: questionAttemptEntity?.id ?? null,
      };
    });
  }

  private getGradingResult(
    questionAttemptEntity: QuestionAttemptEntityUIModel | undefined,
  ): QuestionGradingResultData | null {
    if (!questionAttemptEntity) {
      return null;
    }

    if (questionAttemptEntity.attempt.status !== 'GRADED' || !questionAttemptEntity.attempt.grading_result) {
      return null;
    }

    return {
      awardedMarks: getGradingResultAwardedMarksFromQuestionItems(questionAttemptEntity.questionItems),
      totalMarks: calculateQuestionTotalMarks(
        questionAttemptEntity.questionItems,
        questionAttemptEntity.markCalculationMethod,
      ),
    };
  }
}
